import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IProjetista } from "shared/interfaces/projetista";
import { ISolicitacaoTipologiaListaItem } from "shared/interfaces/solicitacao";

export interface IFiltroAvancado {
  projetistaId?: number;
  linhaId?: number;
  linhaNome?: string;
  grupoTipologiaId?: number;
  descricaoId?: number;
  descricaoBusca?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  filtros: IFiltroAvancado;
  onAplicar: (filtros: IFiltroAvancado) => void;
  projetistas: IProjetista[];
  solicitacoes: ISolicitacaoTipologiaListaItem[];
  isLoading?: boolean;
}

// Função para decodificar texto legado
const decodeLegacyText = (value?: string | null): string => {
  if (!value) return "";
  
  let result = value;
  
  const replacements: Array<[RegExp | string, string]> = [
    [/A\uFFFD\uFFFDO/gi, "AÇÃO"],
    [/a\uFFFD\uFFFDo/gi, "ação"],
    [/\uFFFD\uFFFDO/gi, "ÇÃO"],
    [/\uFFFD\uFFFDo/gi, "ção"],
    [/\uFFFD/g, ""],
    ["Ã§", "ç"],
    ["Ã£", "ã"],
    ["Ãµ", "õ"],
    ["Ã¡", "á"],
    ["Ã©", "é"],
    ["Ã­", "í"],
    ["Ã³", "ó"],
    ["Ãº", "ú"],
    ["Ã¢", "â"],
    ["Ãª", "ê"],
    ["Ã´", "ô"],
    ["Ã ", "à"],
    ["Ã¨", "è"],
    ["Ã¬", "ì"],
    ["Ã²", "ò"],
    ["Ã¹", "ù"],
    ["Ã±", "ñ"],
    ["Ã¼", "ü"],
  ];
  
  for (const [pattern, replacement] of replacements) {
    if (typeof pattern === "string") {
      result = result.split(pattern).join(replacement);
    } else {
      result = result.replace(pattern, replacement);
    }
  }
  
  return result;
};

export const DrawerFiltroAvancado = ({
  open,
  onClose,
  filtros,
  onAplicar,
  projetistas,
  solicitacoes,
  isLoading = false,
}: Props) => {
  // Estado local dos filtros
  const [localFiltros, setLocalFiltros] = useState<IFiltroAvancado>(filtros);

  // Sincroniza com filtros externos quando abre
  useEffect(() => {
    if (open) {
      setLocalFiltros(filtros);
    }
  }, [open, filtros]);

  // Extrair linhas únicas das solicitações filtradas pelo projetista selecionado
  const linhasDisponiveis = useCallback(() => {
    let solicitacoesFiltradas = solicitacoes;
    
    // Se tem projetista selecionado, filtrar
    if (localFiltros.projetistaId) {
      solicitacoesFiltradas = solicitacoes.filter(
        (s) => s.projetistaId === localFiltros.projetistaId
      );
    }
    
    // Extrair linhas únicas
    const linhasMap = new Map<string, string>();
    solicitacoesFiltradas.forEach((s) => {
      if (s.linhaSistema) {
        const linhaDecodificada = decodeLegacyText(s.linhaSistema);
        linhasMap.set(linhaDecodificada, linhaDecodificada);
      }
    });
    
    return Array.from(linhasMap.values()).sort();
  }, [solicitacoes, localFiltros.projetistaId]);

  // Extrair descrições únicas das solicitações filtradas
  const descricoesDisponiveis = useCallback(() => {
    let solicitacoesFiltradas = solicitacoes;
    
    // Filtrar por projetista
    if (localFiltros.projetistaId) {
      solicitacoesFiltradas = solicitacoesFiltradas.filter(
        (s) => s.projetistaId === localFiltros.projetistaId
      );
    }
    
    // Filtrar por linha
    if (localFiltros.linhaNome) {
      solicitacoesFiltradas = solicitacoesFiltradas.filter(
        (s) => decodeLegacyText(s.linhaSistema) === localFiltros.linhaNome
      );
    }
    
    // Extrair descrições únicas
    const descricoesSet = new Set<string>();
    solicitacoesFiltradas.forEach((s) => {
      if (s.descricao) {
        const descDecodificada = decodeLegacyText(s.descricao);
        descricoesSet.add(descDecodificada);
      }
    });
    
    return Array.from(descricoesSet).sort();
  }, [solicitacoes, localFiltros.projetistaId, localFiltros.linhaNome]);

  // Handlers
  const handleProjetistaChange = (event: SelectChangeEvent<number | "">) => {
    const value = event.target.value;
    setLocalFiltros((prev) => ({
      ...prev,
      projetistaId: value === "" ? undefined : (value as number),
      // Limpar filtros dependentes
      linhaNome: undefined,
      descricaoBusca: undefined,
    }));
  };

  const handleLinhaChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setLocalFiltros((prev) => ({
      ...prev,
      linhaNome: value === "" ? undefined : value,
      // Limpar filtros dependentes
      descricaoBusca: undefined,
    }));
  };

  const handleDescricaoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalFiltros((prev) => ({
      ...prev,
      descricaoBusca: value || undefined,
    }));
  };

  // Limpar todos os filtros
  const handleLimpar = () => {
    setLocalFiltros({});
  };

  // Aplicar filtros
  const handleAplicar = () => {
    onAplicar(localFiltros);
    onClose();
  };

  const linhas = linhasDisponiveis();
  const descricoes = descricoesDisponiveis();

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 380, p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Cabeçalho */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            Filtro Avançado
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Corpo dos filtros */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Stack spacing={2.5}>
              {/* Projetista */}
              <FormControl fullWidth size="small">
                <InputLabel>Projetista</InputLabel>
                <Select
                  value={localFiltros.projetistaId ?? ""}
                  onChange={handleProjetistaChange}
                  label="Projetista"
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {projetistas.map((p) => (
                    <MenuItem key={p.idProjetista} value={p.idProjetista}>
                      {p.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Linha */}
              <FormControl fullWidth size="small">
                <InputLabel>Linha</InputLabel>
                <Select
                  value={localFiltros.linhaNome ?? ""}
                  onChange={handleLinhaChange}
                  label="Linha"
                  disabled={linhas.length === 0}
                >
                  <MenuItem value="">
                    <em>Todas</em>
                  </MenuItem>
                  {linhas.map((linha) => (
                    <MenuItem key={linha} value={linha}>
                      {linha}
                    </MenuItem>
                  ))}
                </Select>
                {linhas.length === 0 && localFiltros.projetistaId && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Nenhuma linha encontrada para este projetista
                  </Typography>
                )}
              </FormControl>

              {/* Descrição - campo de texto para busca */}
              <TextField
                fullWidth
                size="small"
                label="Buscar na descrição"
                placeholder="Digite parte da descrição..."
                value={localFiltros.descricaoBusca ?? ""}
                onChange={handleDescricaoChange}
                helperText={
                  descricoes.length > 0
                    ? `${descricoes.length} descrições disponíveis`
                    : localFiltros.projetistaId
                    ? "Nenhuma descrição encontrada"
                    : undefined
                }
              />

              {/* Lista de descrições disponíveis (apenas informativo) */}
              {descricoes.length > 0 && descricoes.length <= 10 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Descrições disponíveis:
                  </Typography>
                  <Box sx={{ maxHeight: 150, overflow: "auto", pl: 1 }}>
                    {descricoes.map((desc, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        sx={{
                          display: "block",
                          py: 0.25,
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                        onClick={() =>
                          setLocalFiltros((prev) => ({
                            ...prev,
                            descricaoBusca: desc,
                          }))
                        }
                      >
                        • {desc}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </Box>

        {/* Rodapé com botões */}
        <Stack spacing={1.5} sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleLimpar}
            fullWidth
            sx={{ textTransform: "none" }}
            disabled={
              !localFiltros.projetistaId &&
              !localFiltros.linhaNome &&
              !localFiltros.descricaoBusca
            }
          >
            Limpar Filtros
          </Button>
          <Button
            variant="contained"
            onClick={handleAplicar}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            APLICAR FILTRO
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
